import { Cache, Clock, Context, Duration, Effect, Exit, Layer } from 'effect'
import {
  type CommandError,
  type HttpStatusError,
  type SDKCommand,
  type SDKResponseFor,
  type SDKRequestFor,
  type STSDK,
  type TimeoutError,
  type TransportError
} from '@st/sdk'
import { envVar } from '~/env'
import { createSdkRequestErrorCaptureHandler } from '~/features/error-reporting/sdk-request-error-capture'
import { ErrorReporting } from './error-reporting'

const MAX_LOCAL_COMMAND_DATA_BYTES = 10_000
const SENSITIVE_COMMANDS = new Set<SDKCommand['type']>(['folders/startKBA', 'folders/submitKBA'])

export type SdkFailure = TransportError | TimeoutError | HttpStatusError

export type SdkSend = <K extends SDKCommand['type']>(
  request: SDKRequestFor<K>
) => Effect.Effect<SDKResponseFor<K>, SdkFailure>

type SdkClientService = {
  send: SdkSend
}

export class RawSdk extends Context.Service<RawSdk, STSDK>()('RawSdk') {}

export class SdkClient extends Context.Service<SdkClient, SdkClientService>()('SdkClient') {}

export type SdkCallHandlers<K extends SDKCommand['type'], Success, Failure> = {
  onSuccess: (response: SDKResponseFor<K>) => Success
  onFailure: (error: SdkFailure) => Failure
}

function call<K extends SDKCommand['type']>(
  request: SDKRequestFor<K>
): Effect.Effect<SDKResponseFor<K>, SdkFailure, SdkClient>
function call<K extends SDKCommand['type'], Success, Failure>(
  request: SDKRequestFor<K>,
  handlers: SdkCallHandlers<K, Success, Failure>
): Effect.Effect<Success | Failure, never, SdkClient>
function call<K extends SDKCommand['type'], Success, Failure>(
  request: SDKRequestFor<K>,
  handlers?: SdkCallHandlers<K, Success, Failure>
) {
  const sent = SdkClient.use((client) => client.send(request))
  if (handlers == undefined) return sent
  return Effect.match(sent, handlers)
}

/**
 * Calls the backend. With handlers, both outcomes become values, so the effect
 * cannot fail: a module passes handlers that return its messages. Without
 * them, the response and its typed failure stay in the effect, for domain
 * code that composes several calls. Either way the client has already
 * reported any failure that points at a bug of ours, so a handler only
 * decides what the person sees.
 */
export const sdk = { call }

export function sdkClientLayer() {
  return Layer.effect(
    SdkClient,
    Effect.gen(function* () {
      const sdk = yield* RawSdk
      const errorReporting = yield* ErrorReporting

      const organizationBySlugCache = yield* Cache.makeWith(
        (organizationSlug: string) =>
          sdk.send({
            type: 'organizations/getOrganizationBySlug',
            organizationSlug
          }),
        {
          capacity: 256,
          // Keep successful lookups hot but never cache failures.
          timeToLive: (exit) => (Exit.isSuccess(exit) ? Duration.infinity : Duration.zero)
        }
      )

      const captureCommandData = Boolean(envVar('NEXT_PUBLIC_SENTRY_TUNNEL'))

      const withRequestBreadcrumb = <A>(
        request: SDKCommand,
        effect: Effect.Effect<A, CommandError>
      ): Effect.Effect<A, CommandError> =>
        Effect.gen(function* () {
          const startedAt = yield* Clock.currentTimeMillis
          const addBreadcrumb = (details: {
            error?: unknown
            level?: 'error'
            outcome: 'succeeded' | 'failed'
            response?: unknown
          }) =>
            Clock.currentTimeMillis.pipe(
              Effect.flatMap((finishedAt) =>
                errorReporting.addBreadcrumb({
                  category: 'sdk.command',
                  message: request.type,
                  data: commandBreadcrumbData({
                    captureCommandData: captureCommandData && !SENSITIVE_COMMANDS.has(request.type),
                    durationMs: finishedAt - startedAt,
                    error: details.error,
                    outcome: details.outcome,
                    request,
                    response: details.response,
                    startedAt
                  }),
                  level: details.level
                })
              )
            )

          return yield* effect.pipe(
            Effect.tap((response) =>
              addBreadcrumb({
                outcome: 'succeeded',
                response
              })
            ),
            Effect.tapError((error) =>
              addBreadcrumb({
                error,
                level: 'error',
                outcome: 'failed'
              })
            )
          )
        })

      const reportFailure = createSdkRequestErrorCaptureHandler({ errorReporting, source: 'app' })

      // Invalid requests and responses are defects. Of the other failures,
      // those that point at a bug of ours are reported here, once, whether or
      // not the caller then handles it.
      const enforceSdkFailureBoundary = <A>(
        request: SDKCommand,
        effect: Effect.Effect<A, CommandError>
      ): Effect.Effect<A, SdkFailure> =>
        effect.pipe(
          Effect.catchTags({
            InvalidRequestError: (error) => Effect.die(error),
            InvalidResponseError: (error) => Effect.die(error)
          }),
          Effect.tapError((error) => reportFailure({ request, error }))
        )

      function send<K extends SDKCommand['type']>(
        req: SDKRequestFor<K>
      ): Effect.Effect<SDKResponseFor<K>, SdkFailure, never>
      function send(req: SDKCommand) {
        if (req.type == 'organizations/getOrganizationBySlug') {
          return enforceSdkFailureBoundary(
            req,
            withRequestBreadcrumb(
              req,
              Cache.get(organizationBySlugCache, req.organizationSlug).pipe(
                Effect.tapError(() =>
                  Cache.invalidate(organizationBySlugCache, req.organizationSlug)
                )
              )
            )
          )
        }
        return enforceSdkFailureBoundary(req, withRequestBreadcrumb(req, sdk.send(req)))
      }

      return {
        send
      }
    })
  )
}

function commandBreadcrumbData({
  captureCommandData,
  durationMs,
  error,
  outcome,
  request,
  response,
  startedAt
}: {
  captureCommandData: boolean
  durationMs: number
  error?: unknown
  outcome: 'succeeded' | 'failed'
  request: SDKCommand
  response?: unknown
  startedAt: number
}) {
  const data: Record<string, unknown> = { durationMs, outcome, startedAt }
  if (!captureCommandData) return data

  data.request = boundedCommandData(request)
  if (outcome == 'succeeded') data.response = boundedCommandData(response)
  if (outcome == 'failed') data.error = boundedCommandData(error)
  return data
}

function boundedCommandData(value: unknown) {
  const json = JSON.stringify(value)
  if (json === undefined) return value
  if (json.length <= MAX_LOCAL_COMMAND_DATA_BYTES) return value
  return { bytes: json.length, truncated: true }
}
